import React, { useMemo } from 'react';
import { cn } from '@rocco/utils/cn';
import { useTypingIndicator } from '../../hooks/use-typing-indicator';

/**
 * Typing indicator component that displays when participants are typing. Shows
 * "Name is typing..." for one person, or "n people are typing..." for multiple.
 * When the conversation ID is not provided or there are no typing participants,
 * this component stays the same size.
 */
export function TypingIndicator({
    className,
    conversationID,
}: {
    className?: string;
    conversationID?: number;
}) {
    const typingParticipants = useTypingIndicator(conversationID ?? 0);
    const typingText = useMemo<{
        content: React.ReactNode;
        ariaLabel: string;
    }>(() => {
        if (typingParticipants.length === 0) {
            return {
                content: '',
                ariaLabel: '',
            };
        }
        if (typingParticipants.length === 1) {
            const name = typingParticipants[0]?.participantName ?? 'Someone';
            return {
                content: (
                    <>
                        <strong>{name}</strong> is typing
                    </>
                ),
                ariaLabel: `${name} is typing`,
            };
        }
        return {
            content: `${typingParticipants.length} people are typing`,
            ariaLabel: `${typingParticipants.length} people are typing`,
        };
    }, [typingParticipants]);

    return (
        <div
            className={cn(
                'flex h-[14px] items-center text-[11px] leading-tight text-black/70',
                className
            )}
            aria-live="polite"
            aria-label={typingText.ariaLabel}
        >
            <span className="truncate">{typingText.content}</span>
        </div>
    );
}
